<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import type { Breadcrumb, Section, Brand, ProductCategory, Pagination } from '@storefront/types';
  import type { ProductVariantResource, ProductForReview, ProductResource } from '@storefront/resources';
  import { ProductType } from '@othercommerce/domain';
  import { useGoogleTagManager, viewItemEvent } from '@storefront/storefront';
  import { useShared } from '@othercommerce/foundation';

  type ReviewsSummary = {
    1: { count: number, percent: number },
    2: { count: number, percent: number },
    3: { count: number, percent: number },
    4: { count: number, percent: number },
    5: { count: number, percent: number },
  }

  interface Props {
    breadcrumbs: Breadcrumb[],
    sections: Section[],
    product: ProductResource,
    variant: ProductVariantResource,
    set: ProductResource | null,
    brand: Brand,
    category?: ProductCategory | undefined,
    recommended: any[],
    posts: any[],
    reviews: Pagination<ProductForReview>,
    reviewsSummary: ReviewsSummary,
    reviewAvailable: boolean,
    freeDeliveryRemaining: number | null | string,
  }

  const props = defineProps<Props>();
  const shared = useShared();
  const gtm = useGoogleTagManager();

  const wrapperHtmlElement = ref();


  watch(
    () => props.variant,
    (variant) => {
      gtm.push(viewItemEvent(variant, shared.value.activeCurrencyCode));
    },
    {
      immediate: true,
    },
  );
</script>
<template>
  <main id="main">
    <div class="container product-single-top-wrapper">
      <SfBreadcrumbs :breadcrumbs="breadcrumbs" />
      <FreeDeliveryRemaining :freeDeliveryRemaining />
    </div>
    <ShopProductViewBanner v-if="product.type !== ProductType.FLEXIBLE" :key="product.id" :variant="variant" :wrapper="wrapperHtmlElement" />
    <template v-if="set">
      <div class="product-set-available container">
        <div :class="{'product-set-available__content--photo': set.photo}" class="product-set-available__content">
          <div v-if="set.photo" class="photo">
            <MediaPicture :photo="set.photo" sizes="160x160 2x, 80x80" />
          </div>
          <div class="description">
            <p class="product-set-available-header">{{ $t('SHOP.PRODUCT.HEADER.CHEAPER_IN_SET') }}</p>
            <p class="product-set-title">{{ set.title }}</p>
            <div v-if="set.descriptionShort" class="product-set-description" v-html="set.descriptionShort" />
          </div>
          <div class="action">
            <SfButton :href="$route('web.product.show', { slug: set.slug })" primary>
              {{ $t('SHOP.PRODUCT.ACTION.CHECK_SET') }}
            </SfButton>
          </div>
        </div>
      </div>
    </template>
    <div ref="wrapperHtmlElement">
      <ShopProductViewDetails :key="product.id" :brand :category :freeDeliveryRemaining :product="product" :variant="variant" />
    </div>
    <ShopProductViewDescription :key="product.id" :recommended :reviewAvailable :reviews :reviewsSummary :sections :variant />
  </main>

  <RouterView />
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .product-single-top-wrapper {
    display: flex;
    flex-direction: column;

    .free-delivery-remaining {
      margin-bottom: 1rem;
    }
  }

  .product-set-available {
    padding: var(--section-padding);
    background: var(--section-background);
    border-radius: var(--section-border-radius);
    border-inline: var(--section-border-inline);
    border-block: var(--section-border-block);
    margin-bottom: var(--sf-section-renderer-gap);

    &__content {
      display: flex;
      gap: 2rem;
    }

    .photo {
      flex: 0 0 80px;
      width: 80px;
      height: 80px;

      :deep(img) {
        display: flex;
        width: 80px;
        height: 80px;
        object-fit: contain;
      }
    }

    .description {
      width: 100%;

      .product-set-available-header {
        text-transform: uppercase;
        color: var(--primary-color);
        font-weight: var(--font-weight-semi);
        font-size: 1rem;
      }

      .product-set-title {
        font-weight: var(--font-weight-bold);
        font-size: 0.875rem;
      }

      .product-set-description {
        margin-top: 0.5rem;
        font-size: 0.75rem;

        :deep(p) {
          font-size: 0.75rem;

          b, strong {
            color: var(--primary-color);
          }
        }
      }
    }

    @include media.query(lg) {
      flex-direction: column;
      gap: 1rem;

      &__content {
        padding: 0 0.625rem;

        &--photo {
          display: grid;
          grid-template-columns: 80px 1fr;
          gap: 1rem 0.75rem;

          .action {
            grid-column: 1 /-1;
            justify-self: end;
          }
        }
      }

      .description .product-set-description {
        margin-top: 0.125rem;
      }

      .action {
        align-self: end;
      }
    }
  }
</style>
